import type { ErrorCode } from '@pxc/shared';
import { ApiError, NetworkError } from '../services/api.js';
import { formatDuration } from './format.js';
import { AdDismissedError, AdTooSoonError, AdUnavailableError, type AdUnavailableReason } from '../services/ads.js';
import { useT, type CatalogKey, type Translator } from '../i18n/index.js';

/** Our own reasons for having no ad to show, in the reader's language. */
const AD_UNAVAILABLE_COPY: Record<AdUnavailableReason, CatalogKey> = {
  noSdk: 'error.AD_PROVIDER_UNAVAILABLE.noSdk',
  noBlock: 'error.AD_PROVIDER_UNAVAILABLE.noBlock',
  notLoaded: 'error.AD_PROVIDER_UNAVAILABLE.notLoaded',
  unreachable: 'error.AD_PROVIDER_UNAVAILABLE.unreachable',
  noFill: 'error.AD_PROVIDER_UNAVAILABLE.noFill',
};

/**
 * Machine error codes → human copy (§32), in the reader's language (§20).
 *
 * This is how a message the *backend* produced ends up translated without the
 * backend having to carry six catalogues. Every failure arrives as a readable
 * English sentence *and* a machine code; the client prefers its own localised
 * copy for that code, and falls back to the server's sentence only for a code
 * it does not know. So the server stays English — which is what its logs and
 * any future API consumer want — and the user reads their own language.
 *
 * `useErrorCopy` is what a component uses. It returns a function with the same
 * shape the old `describeError` had, so a call site is one added line rather
 * than a rewrite.
 *
 * There is no untranslated path any more: the English copy that used to live
 * in a map here is in the catalogue with the other five languages, under
 * `error.<CODE>.title` and `error.<CODE>.body`. Keeping a second copy of it
 * meant two places to edit and one of them silently winning.
 */
export interface ErrorMessage {
  title: string;
  description?: string;
  /** True when retrying the same action is likely to help. */
  retryable: boolean;
  /** The retry button's word, for the error screens that spread this in. */
  retryLabel?: string;
}

/**
 * The same description, translated.
 *
 * Returns a function rather than a value so a component can describe several
 * errors, and so every existing `describeError(error)` call site keeps its
 * shape.
 */
export function useErrorCopy(): (error: unknown) => ErrorMessage {
  const { t, maybe } = useT();
  return (error: unknown) => ({ ...translateError(error, t, maybe), retryLabel: t('common.retry') });
}

/** Shared by the hook and by anything holding a translator already. */
export function translateError(
  error: unknown,
  t: Translator['t'],
  maybe: Translator['maybe'],
): ErrorMessage {
  const generic: ErrorMessage = {
    title: t('error.INTERNAL_ERROR.title'),
    description: t('error.INTERNAL_ERROR.body'),
    retryable: true,
  };

  if (error instanceof NetworkError) {
    return {
      title: t('common.noConnection'),
      description: t('common.noConnectionHint'),
      retryable: true,
    };
  }

  if (error instanceof AdUnavailableError) {
    // Our own reasons are said in the reader's language. Otherwise the
    // provider's own message is the useful part, and it is not ours to
    // translate — but the heading is.
    return {
      title: t('error.AD_PROVIDER_UNAVAILABLE.title'),
      description: error.reason
        ? t(AD_UNAVAILABLE_COPY[error.reason])
        : error.message || t('error.AD_PROVIDER_UNAVAILABLE.body'),
      retryable: true,
    };
  }

  if (error instanceof AdTooSoonError) {
    return {
      title: t('error.AD_TOO_SOON.title'),
      description: t('error.AD_TOO_SOON.body'),
      retryable: true,
    };
  }

  if (error instanceof AdDismissedError) {
    return {
      title: t('error.AD_DISMISSED.title'),
      description: t('error.AD_DISMISSED.body'),
      retryable: true,
    };
  }

  if (error instanceof ApiError) {
    const values = errorValues(error);
    const title = maybe(`error.${error.code}.title`, values);
    const body = maybe(`error.${error.code}.body`, values);
    return {
      // A code with no entry falls back to the server's own sentence rather
      // than to nothing: an untranslated message beats a blank one.
      title: title ?? generic.title,
      description: body ?? error.message,
      retryable: error.status >= 500 || error.status === 429 || error.code === 'AD_VERIFICATION_FAILED',
    };
  }

  return generic;
}

/**
 * What an error's copy may fill in: the plain values the server sent with it
 * and, for a moment the reader has to wait for (`nextAt`), how long that is
 * from now, written as the app writes durations.
 */
function errorValues(error: ApiError): Record<string, string | number> {
  const values: Record<string, string | number> = {};
  if (error.details && typeof error.details === 'object' && !Array.isArray(error.details)) {
    for (const [name, value] of Object.entries(error.details)) {
      if (typeof value === 'string' || typeof value === 'number') values[name] = value;
    }
  }
  if (typeof values.nextAt === 'number') values.wait = formatDuration((values.nextAt - Date.now()) / 1000);
  return values;
}
